import { toLocalDateTimeValue } from './locationSchedule';

const days = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const dayLength = 86400000;
const weekLength = 7 * dayLength;
const minutes = value => {
  if (value == null || value === '') return null;
  if (String(value).includes(':')) {
    const [hours, remainder] = String(value).split(':').map(Number);
    return hours * 60 + remainder;
  }
  return Math.floor(Number(value) / 100) * 60 + Number(value) % 100;
};
const clock = value => `${String(Math.floor(value / 60)).padStart(2, '0')}:${String(value % 60).padStart(2, '0')}`;
const subtract = (windows, blocked) => blocked.reduce((remaining, block) => remaining.flatMap(window => {
  if (window.end <= block.start || window.start >= block.end) return [window];
  return [
    ...(window.start < block.start ? [{ start: window.start, end: block.start }] : []),
    ...(window.end > block.end ? [{ start: block.end, end: window.end }] : [])
  ];
}), windows);
const merge = windows => windows.slice().sort((first, second) => first.start - second.start).reduce((result, window) => {
  const previous = result[result.length - 1];
  if (previous && window.start <= previous.end) previous.end = Math.max(previous.end, window.end);
  else result.push({ ...window });
  return result;
}, []);
const overlaps = (first, second, travelTime) => first.start < second.end + travelTime && second.start < first.end + travelTime;
const overrideTimestamp = (schedule, value) => {
  if (schedule.aircraftId == null) return `${toLocalDateTimeValue(value)}Z`;
  const text = String(value || '').replace(' ', 'T');
  return /(?:Z|[+-]\d\d:\d\d)$/i.test(text) ? text : `${text}Z`;
};
const overrides = schedule => (schedule.scheduleOverrides || schedule.overrides || []).map(item => ({
  type: item.overrideType,
  start: Date.parse(overrideTimestamp(schedule, item.startDateTime ?? item.startDatetime)),
  end: Date.parse(overrideTimestamp(schedule, item.endDateTime ?? item.endDatetime))
})).filter(item => Number.isFinite(item.start) && Number.isFinite(item.end) && item.end > item.start);
const weekly = schedule => days.flatMap((day, index) => {
  const start = minutes(schedule[`${day}Start`]);
  const end = minutes(schedule[`${day}End`]);
  return start == null || end == null || end <= start ? [] : [{ start: index * dayLength + start * 60000, end: index * dayLength + end * 60000 }];
});
const effective = (schedule, exceptions, start, end) => {
  const windows = exceptions.filter(item => item.type === 'on' && item.start < end && item.end > start)
    .map(item => ({ start: Math.max(start, item.start), end: Math.min(end, item.end) }));
  const first = new Date(start); first.setUTCHours(0, 0, 0, 0);
  for (let midnight = first.getTime(); midnight < end; midnight += dayLength) {
    const day = days[new Date(midnight).getUTCDay()];
    const lower = minutes(schedule[`${day}Start`]);
    const upper = minutes(schedule[`${day}End`]);
    if (lower == null || upper == null) continue;
    const lowerBound = midnight + lower * 60000;
    const upperBound = midnight + upper * 60000;
    if (lowerBound < end && upperBound > start) windows.push({ start: Math.max(start, lowerBound), end: Math.min(end, upperBound) });
  }
  return merge(subtract(windows, exceptions.filter(item => item.type === 'off')));
};

export const isAircraftBlockedAtOtherLocation = (schedules, aircraftId, locationId, start, end = new Date(start.getTime() + 1)) => {
  const travelTime = 60 * 60000;
  const lower = start.getTime();
  const upper = end.getTime();
  return schedules.some(schedule => Number(schedule.aircraftId) === Number(aircraftId) &&
    String(schedule.locationId) !== String(locationId) &&
    effective(schedule, overrides(schedule), lower - travelTime, upper + travelTime)
      .some(window => lower < window.end + travelTime && upper > window.start - travelTime));
};

export const getAircraftLocationAvailabilityError = ({ aircraftId, locationId, start, end, schedules, locations, loading, error }) => {
  if (!aircraftId || !locationId || locations.length < 2) return '';
  if (error) return error;
  if (loading || !schedules || locations.some(location => !Object.hasOwn(schedules, String(location.id)))) {
    return 'Wait for aircraft schedules at all locations to load.';
  }
  if (!Number.isFinite(start?.getTime()) || (end && (!Number.isFinite(end.getTime()) || end <= start))) {
    return 'Select a valid aircraft reservation time.';
  }
  return isAircraftBlockedAtOtherLocation(Object.values(schedules).flat(), aircraftId, locationId, start, end)
    ? 'Aircraft is scheduled at another location or within the 60-minute travel buffer.' : '';
};

const conflicts = (preferred, other, firstOverrides, otherOverrides, travelTime) => {
  const firstWeekly = weekly(preferred);
  const otherWeekly = weekly(other);
  if ([-weekLength, 0, weekLength].some(offset => firstWeekly.some(first => otherWeekly.some(second => overlaps(first, { start: second.start + offset, end: second.end + offset }, travelTime))))) return true;
  return [...firstOverrides, ...otherOverrides].flatMap(item => [item.start, item.end]).some(boundary => {
    const start = boundary - weekLength - travelTime;
    const end = boundary + weekLength + travelTime;
    return effective(preferred, firstOverrides, start, end).some(first => effective(other, otherOverrides, start, end).some(second => overlaps(first, second, travelTime)));
  });
};

export const reconcileLocationSchedule = (preferred, other) => {
  const travelTime = (preferred.aircraftId != null ? 60 : 30) * 60000;
  const preferredOverrides = overrides(preferred);
  const otherOverrides = overrides(other);
  if (!conflicts(preferred, other, preferredOverrides, otherOverrides, travelTime)) return null;
  const blocked = [-weekLength, 0, weekLength].flatMap(offset => weekly(preferred).map(window => ({ start: window.start + offset - travelTime, end: window.end + offset + travelTime })));
  const remaining = subtract(weekly(other), blocked);
  const adjusted = { ...other, scheduleOverrides: [] };
  days.forEach((day, index) => {
    const segments = remaining.filter(window => window.start >= index * dayLength && window.end <= (index + 1) * dayLength)
      .sort((first, second) => (second.end - second.start) - (first.end - first.start) || second.start - first.start);
    adjusted[`${day}Start`] = segments.length ? clock(Math.round((segments[0].start - index * dayLength) / 60000)) : null;
    adjusted[`${day}End`] = segments.length ? clock(Math.round((segments[0].end - index * dayLength) / 60000)) : null;
  });
  const dates = new Set();
  for (const exception of [...preferredOverrides, ...otherOverrides]) {
    const first = new Date(exception.start - travelTime); first.setUTCHours(0, 0, 0, 0);
    for (let date = first.getTime(); date < exception.end + travelTime; date += dayLength) dates.add(date);
  }
  const differences = { on: [], off: [] };
  for (const date of [...dates].sort((first, second) => first - second)) {
    const preferredWindows = effective(preferred, preferredOverrides, date - travelTime, date + dayLength + travelTime)
      .map(window => ({ start: window.start - travelTime, end: window.end + travelTime }));
    const desired = merge(subtract(effective(other, otherOverrides, date, date + dayLength), preferredWindows));
    const baseline = effective(adjusted, [], date, date + dayLength);
    differences.on.push(...subtract(desired, baseline));
    differences.off.push(...subtract(baseline, desired));
  }
  for (const type of ['on', 'off']) adjusted.scheduleOverrides.push(...merge(differences[type]).map(window => ({
    overrideType: type,
    startDateTime: new Date(window.start).toISOString().slice(0, 19),
    endDateTime: new Date(window.end).toISOString().slice(0, 19)
  })));
  return adjusted;
};

export const scheduleWritePayload = schedule => ({
  ...(schedule.id ? { id: schedule.id } : {}),
  [schedule.aircraftId != null ? 'aircraftId' : 'userId']: Number(schedule.aircraftId ?? schedule.userId),
  locationId: Number(schedule.locationId),
  ...Object.fromEntries(days.flatMap(day => ['Start', 'End'].map(bound => {
    const value = minutes(schedule[`${day}${bound}`]);
    return [`${day}${bound}`, value == null ? null : clock(value)];
  }))),
  scheduleOverrides: (schedule.scheduleOverrides || schedule.overrides || []).map(item => ({
    overrideType: item.overrideType,
    startDateTime: schedule.aircraftId != null ? new Date(overrideTimestamp(schedule, item.startDateTime ?? item.startDatetime)).toISOString() : toLocalDateTimeValue(item.startDateTime ?? item.startDatetime),
    endDateTime: schedule.aircraftId != null ? new Date(overrideTimestamp(schedule, item.endDateTime ?? item.endDatetime)).toISOString() : toLocalDateTimeValue(item.endDateTime ?? item.endDatetime)
  }))
});

export const saveLocationSchedules = async ({ selected, schedules, client, sessionId }) => {
  const resourceKey = selected.aircraftId != null ? 'aircraftId' : 'userId';
  const endpoint = selected.aircraftId != null ? '/availability-schedule' : '/work-schedule';
  const adjusted = schedules.filter(item => Number(item[resourceKey]) === Number(selected[resourceKey]) && Number(item.locationId) !== Number(selected.locationId))
    .map(item => ({ original: item, adjusted: reconcileLocationSchedule(selected, item) })).filter(item => item.adjusted);
  const written = [];
  const originalSelected = schedules.find(item => Number(item[resourceKey]) === Number(selected[resourceKey]) && Number(item.locationId) === Number(selected.locationId));
  let attemptingSelected = false;
  const write = async payload => {
    let response;
    try {
      response = await client(endpoint, { method: 'POST', headers: { 'session-id': sessionId, 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    } catch (error) { error.unknownWrite = true; throw error; }
    let result;
    try { result = await response.json(); }
    catch {
      const error = new Error('Unable to confirm the schedule save.');
      error.unknownWrite = response.ok;
      throw error;
    }
    if (!response.ok) throw new Error(result?.error || 'Unable to save schedule.');
    return result;
  };
  try {
    for (const item of adjusted) {
      written.push(item);
      await write(scheduleWritePayload(item.adjusted));
    }
    attemptingSelected = true;
    const result = await write(selected);
    return { result, adjustedCount: adjusted.length, savedSchedules: [...adjusted.map(item => item.adjusted), { ...selected, id: result.id ?? selected.id }] };
  } catch (error) {
    const failedRestorations = [];
    if (attemptingSelected && originalSelected) {
      try {
        const original = scheduleWritePayload(originalSelected);
        for (const field of ['rate', 'payRate']) if (Object.hasOwn(selected, field)) original[field] = originalSelected[field] ?? null;
        await write(original);
      } catch { failedRestorations.push(originalSelected.locationId); }
    }
    for (const item of written.reverse()) {
      try { await write(scheduleWritePayload(item.original)); }
      catch { failedRestorations.push(item.original.locationId); }
    }
    error.reloadSchedules = failedRestorations.length > 0 || (attemptingSelected && !originalSelected && !!error.unknownWrite);
    if (failedRestorations.length) error.message += ' Some location schedules could not be restored. Reload schedules before trying again.';
    else if (error.reloadSchedules) error.message += ' Reload schedules to verify the saved state before trying again.';
    throw error;
  }
};